import { Location as LocationAngularCommon, TitleCasePipe } from "@angular/common";
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import { DataTable, GetDataEvent, UpdateDataEvent } from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { getUpdatedValues, isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { getDisplayedColumnsAssignAssets } from "../../assets/asset";
import { AssetTableService } from "../../assets/asset-table.service";
import { AssetService } from "../../assets/asset.service";
import { Asset, ContractAsset, TotalContractAssetsPrices } from "../../assets/asset.type";
import { MaintenanceProvider } from "../../maintenance-provider/maintenance-provider.types";
import { ContractAttachmentTableService } from "../contract-attachment-table.service";
import { ContractService } from "../contract.service";
import { Contract, ContractAttachment, ContractStatus } from "../contract.type";
import { CreateContractService } from "../create-contract/create-contract.service";
import { ContractAttachmentsService } from "./contract-attachments.service";

@Component({
  selector: "app-manage-contract",
  templateUrl: "./manage-contract.component.html",
  styleUrls: ["./manage-contract.component.scss"],
  providers: [CreateContractService, TitleCasePipe, ContractAttachmentsService],
})
export class ManageContractComponent implements OnInit {
  @ViewChildren(DataTableComponent) tables: QueryList<DataTableComponent<unknown>>;
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

  currentUser: UserInfo;
  versionForm: FormGroup;
  contract: Contract;
  contractId: string;
  location: Location;
  maintenanceProviders: MaintenanceProvider[];
  questions: QuestionBase<string | boolean | number>[] = [];
  totalAssetsPrices: TotalContractAssetsPrices;
  contractAssetsTableConfig: DataTable<ContractAsset>;
  contractAttachmentsTableConfig: DataTable<ContractAttachment>;
  isEditing = false;
  onlyReseller = false;
  hideReseller = true;
  showAttachmentTab = false;
  canAddOrUpdateContractAttachment = false;
  operation: Operation;
  public isRequiredField = isRequiredField;

  constructor(
    public dialog: MatDialog,
    public assetTableService: AssetTableService,
    public contractAttachmentTableService: ContractAttachmentTableService,
    public dialogRef: MatDialogRef<GenericModalComponent | DeleteModalComponent>,
    public locationAngularCommon: LocationAngularCommon,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private loader: LoadingService,
    private createContractService: CreateContractService,
    private contractService: ContractService,
    private authenticationService: AuthenticationService,
    private assetService: AssetService,
    private contractAttachmentsService: ContractAttachmentsService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.operation = Operation.update;
  }

  //#region Lifecycle Methods

  ngOnInit(): void {
    this.contractId = this.route.snapshot.paramMap.get("id");
    this.showAttachmentTab = !this.currentUser.isCompanyOrViewAsCompany();
    this.getContract();
  }

  //#endregion

  //#region Handlers
  onGoBack = (): void => this.goBack();

  onToggleManageContract = (): void => this.toggleManageContract();

  onUpdateContract = (): void => {
    if (this.dynamicForm.form.valid) {
      this.updateContract();
    }
  };

  onChangeContract = (event: AnyObject): void => {
    this.updateStatusAndDates(event);
  };

  onDeleteContract = (): void => this.deleteContract();

  /** @workaround fix an issue when table columns are not displayed properly in the table */
  onTabChange = (): void => this.resetTabs();

  onGetWelcomeLetter = () => this.getWelcomeLetter();

  onUpdateContractAsset = (data) => this.updateContractAsset(data);

  //#endregion

  //#region Getters
  /** Populate form data */
  getContract = (): void => {
    this.contractService
      .getContract(this.contractId, {
        relations: { company: { partner: true }, maintenanceProvider: true },
      })
      .subscribe(
        (contract) => {
          if (this.currentUser.isGlobal()) {
            this.contractService
              .getTotalPrices(this.contractId)
              .toPromise()
              .then((totalAssetsPrices) => {
                this.contract = { ...contract, ...totalAssetsPrices };
                this.performAdditionalOperations();
              })
              .catch((error) => {
                this.handleContractError(error);
              });
          } else {
            this.contract = contract;
            this.performAdditionalOperations();
          }
        },
        (error) => {
          this.handleContractError(error);
        },
      );
  };

  /** Populate contract assets table */
  getContractAssets = (event: GetDataEvent<ContractAsset>): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getContractAssets({
        ...queryParams,
        where: {
          ...queryParams?.where,
          contractId: this.contract.id,
        },
        relations: {
          internalProvider: true,
          sla: true,
          contract: { maintenanceProvider: true },
          asset: { company: { partner: true }, location: true, type: { model: { oem: true } } },
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate attachments table */
  getContractAttachments = (event: GetDataEvent<ContractAttachment>): void => {
    const { onError, onResult, queryParams } = event;
    this.contractService
      .getContractAttachments(this.contractId, {
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  //#endregion

  //#region Component Helpers

  /* Only users with admin and manager rights can edit
    As a company or partner cannot edit if it is associated with a channelworks contract */
  canEdit = (): boolean => {
    const partner = this.contract.company.partner;
    if (
      (this.currentUser.isCompany() && this.contract?.maintenanceProvider.isChannelWorks) ||
      (this.currentUser.isPartner() && this.contract.maintenanceProvider.isChannelWorks && !partner.isResellerNeeded)
    )
      return false;
    return allowUpdateEntity(this.contract, this.currentUser);
  };

  /* Only users with admin rights can delete
    As a company or partner cannot be deleted if it is associated with a channelworks contract */

  canDelete = (contract: Contract): boolean => {
    if (this.currentUser.isGlobal() || (!this.currentUser.isGlobal() && !contract?.maintenanceProvider?.isChannelWorks))
      return allowDeleteEntity(contract, this.currentUser);
    return false;
  };

  showActionBanner = (): boolean => {
    return this.canDelete(this.contract) || this.canEdit();
  };

  /**
   * Get/set the class name
   */
  getManageFormClass = (): string => {
    const partialClass = "manage-contract-form-";
    if (this.currentUser.isSalesRepUser()) {
      return `${partialClass}sales-rep`;
    }
    if (this.currentUser.isCompany()) {
      return `${partialClass}company`;
    }
    if (this.currentUser.isPartner(true)) {
      if (this.currentUser.partner?.isResellerNeeded) {
        return `${partialClass}partner-reseller`;
      }
      return `${partialClass}partner`;
    }
    if (this.currentUser.isGlobal()) {
      return `${partialClass}global`;
    }
  };

  //#endregion

  //#region Asset Tab

  private setAssetsTab = (): void => {
    this.contractAssetsTableConfig = this.assetTableService.getContractAssetsTableConfig({
      entities: { contract: this.contract },
      selection: { hidden: !this.canModifyAssets() },
      actionColumn: { delete: { hidden: true } },
    });

    this.contractAssetsTableConfig = {
      ...this.contractAssetsTableConfig,
      actionColumn: [
        ...this.contractAssetsTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkAsset,
          hidden: !this.canModifyAssets(),
        },
      ],
      toolBar: {
        ...this.contractAssetsTableConfig.toolBar,
        selectAllRows: {
          hidden: !this.canModifyAssets(),
        },
        addItem: {
          hidden: !this.canModifyAssets(),
          function: this.addAssets,
        },
        bulkUnlink: {
          hidden: !this.canModifyAssets(),
          function: this.unlinkAssets,
        },
      },
    };
  };

  private addAssets = (table: DataTableComponent<Asset>): void => {
    // Assign companies to the user (used in the popup table)
    const action = (assetsTable: DataTableComponent<Asset>) => {
      if (assetsTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { assetIds: assetsTable.config.selection.selectedRows.map((asset) => asset.id) };

        this.contractService
          .linkAssets(this.contract.id, body)
          .subscribe(
            () => {
              this.notificationService.success("Asset was successfully assigned to a contract");
              table.search();
              this.updateTotalPrices();
            },
            (error) => this.notificationService.error(`Couldn't link assets to contract: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Asset> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link assets to ${this.contract.name}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.assetTableService.getAssetsTableConfig({
              hidden: ["partner.name", "dmr", "monitoring"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
              shown: getDisplayedColumnsAssignAssets(this.currentUser),
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Asset>) => {
                return this.assetTableService.getAvailableAssetsToLinkToContract(this.contract.id, {
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: { ...event.queryParams.where },
                    relations: { contract: { maintenanceProvider: true }, company: true, location: true },
                    select: { id: false },
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private unlinkAsset = (contractAsset: ContractAsset, table: DataTableComponent<ContractAsset>): void => {
    const where = { id: contractAsset.id };

    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "asset",
        data: contractAsset,
        unlink: true,
        fullUrl: `${this.contractService.basePath}/${this.contract.id}/assets`,
        body: { where },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
        this.updateTotalPrices();
      }
    });
  };

  private unlinkAssets = (table: DataTableComponent<ContractAsset>): void => {
    const areAllRowsSelected = table.allRowsSelected;
    const searchFilter = table.searchBar.first.getValues() || {};
    const contractAssetIds = table.config.selection.selectedRows.map((contractAsset) => contractAsset.id);
    const where = areAllRowsSelected ? searchFilter : { id: { $in: contractAssetIds } };

    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        count: contractAssetIds.length || table.config.pagination.totalItems,
        name: "assets",
        unlink: true,
        fullUrl: `${this.contractService.basePath}/${this.contract.id}/assets`,
        body: { where },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.config.selection.selectedRows = [];
        table.search({ isDeletingOrUnlinking: true });
        this.updateTotalPrices();
      }
    });
  };

  private updateTotalPrices() {
    if (this.currentUser.isGlobal()) {
      this.contractService.getTotalPrices(this.contractId).subscribe((assetPrices) => {
        this.totalAssetsPrices = assetPrices;
        this.contract = { ...this.contract, ...assetPrices };
        this.dynamicForm.replaceOrAppendQuestion(
          this.createContractService.generateTotalPriceQuestion(
            "totalChannelWorksCost",
            "Total CW Cost",
            13,
            this.currentUser,
            this.contract,
          ),
        );
        this.dynamicForm.replaceOrAppendQuestion(
          this.createContractService.generateTotalPriceQuestion(
            "totalPartnerPrice",
            "Total Partner Price",
            this.currentUser.isPartner() ? 14 : 15,
            this.currentUser,
            this.contract,
          ),
        );
      });
    }
  }
  private updateContractAsset(event: UpdateDataEvent<ContractAsset>): void {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      // If startDate is updated then we have to pass its endDate too
      // else if endDate is updated then we have to pass its startDate too
      if (updatedValues.startDate) {
        updatedValues.endDate = row.endDate;
      } else if (updatedValues.endDate) {
        updatedValues.startDate = row.startDate;
      }
      this.assetService.updateContractAsset(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.updateTotalPrices();
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  }
  //#endregion

  //#region Attachment Tab
  private setAttachmentsTab = (): void => {
    this.contractAttachmentsTableConfig = this.contractAttachmentTableService.getContractAttachmentsTableConfig();
    this.contractAttachmentsTableConfig = {
      ...this.contractAttachmentsTableConfig,
      toolBar: {
        ...this.contractAttachmentsTableConfig.toolBar,
        addItem: {
          function: this.addAttachment,
          hidden: !this.canAddOrUpdateContractAttachment,
        },
      },
    };
  };

  private addAttachment = (table: DataTableComponent<ContractAttachment>): void => {
    const questions: QuestionBase<string | boolean>[] = [];

    this.contractAttachmentsService
      .generateQuestions({
        isDescriptionEditable: true,
        hideFile: false,
        isNameEditable: true,
      })
      .subscribe((qs) => {
        Object.assign(questions, qs);
      });

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      restoreFocus: false,
      data: {
        title: "Add Contract Attachment",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: ContractAttachment) => {
                this.createAttachment(values, table);
              },
            },
          ],
        },
      },
    });
  };

  private createAttachment = (data: ContractAttachment, table: DataTableComponent<ContractAttachment>): void => {
    this.loader.show();
    const extension = data.file.name.split(".").pop();
    this.contractService.getS3Url(this.contract.id, extension).subscribe(
      (observer) => {
        this.contractAttachmentsService.uploadFile(observer.url, data.file).subscribe(
          () => {
            this.contractService
              .createAttachment(
                {
                  name: data.name,
                  description: data.description,
                  url: observer.name,
                },
                this.contract.id,
              )
              .subscribe(
                () => {
                  this.loader.hide();
                  this.notificationService.success("Attachment added successfully");
                  this.dialogRef.close();
                  table.search();
                  this.contractService.getContractAttachments(this.contract.id);
                },
                (_err) => {
                  this.loader.hide();
                  this.notificationService.error(`Could not create attachment ${_err}`);
                },
              );
          },
          () => {
            this.loader.hide();
            this.notificationService.error("Could not create attachment");
          },
        );
      },
      () => {
        this.loader.hide();
        this.notificationService.error("Could not create attachment");
        table.search();
      },
    );
  };

  private canAddOrUpdateAttachment(): void {
    const initialCondition = this.currentUser.hasRight(["admin", "manager"]);
    this.canAddOrUpdateContractAttachment = initialCondition;
    if (this.currentUser.isPartnerOrViewAsPartner()) {
      this.canAddOrUpdateContractAttachment = initialCondition && !this.contract.maintenanceProvider.isChannelWorks;
    }
  }
  //#endregion

  //#region Others Private Methods
  private setTabs = (): void => {
    this.setAssetsTab();
    this.setAttachmentsTab();
  };

  private goBack = (): void => this.locationAngularCommon.back();

  private toggleManageContract = (): void => {
    this.isEditing = !this.isEditing;
  };

  private updateContract = (): void => {
    const data = getUpdatedValues(this.dynamicForm.form, this.contract) as Contract;

    // Company is not required
    delete data.companyId;

    if (data.endDate) {
      data.startDate = this.dynamicForm.form.get("startDate").value;
    } else if (data.startDate) {
      data.endDate = this.dynamicForm.form.get("endDate").value;
    }

    if (data) {
      this.loader.show();
      this.contractService
        .updateContract(this.contract.id, data)
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          (contract: Contract) => {
            this.contractId = contract.id;
            this.contract = { ...this.contract, ...contract };
            this.isEditing = false;
            this.notificationService.success("Contract updated");
          },
          (error) => this.notificationService.error(error || "Could not update contract"),
        )
        .add(() => this.loader.hide());
    }
  };

  private deleteContract = (): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "contract",
        data: this.contract,
        url: this.contractService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/contracts"]);
      }
    });
  };

  private buildForm = (contract: Contract): void => {
    this.loader.show();
    this.createContractService
      .generateQuestions({
        ...(this.currentUser.isSalesRepUser() && {
          companyValue: this.contract.companyId,
          partnerSkip: true,
          companySkip: true,
          editMode: true,
          ...(!!contract.maintenanceProvider?.isChannelWorks && {
            maintenanceProviderValue: contract.maintenanceProvider,
          }),
          showContractNumber: true,
        }),
        ...(this.currentUser.isCompany() && {
          companyValue: this.currentUser.companyId,
          companySkip: true,
          partnerSkip: true,
          ...(!!contract.maintenanceProvider?.isChannelWorks && {
            maintenanceProviderValue: contract.maintenanceProvider,
          }),
          skipMaintenanceProvider: true,
          showContractNumber: true,
        }),
        ...(this.currentUser.isPartner(true) && {
          partnerValue: this.currentUser.partnerId,
          partnerSkip: true,
          ...(!!contract.maintenanceProvider?.isChannelWorks && {
            maintenanceProviderValue: contract.maintenanceProvider,
          }),
          showContractNumber: true,
        }),
        ...(this.currentUser.isGlobal() && {
          companyValue: contract.companyId,
          partnerValue: contract.company.partnerId,
          partnerSkip: false,
          companySkip: true,
          editMode: true,
          showContractNumber: true,
          partnerDisabled: true,
        }),
        isManageView: true,
        currentUser: this.currentUser,
        contract,
        statusValue: contract.status,
        companyEdit: false,
        hideReseller: this.hideReseller,
        onlyReseller: this.onlyReseller,
        version: this.contract.version,
        operation: this.operation,
      })
      .subscribe((qs) => {
        this.questions = qs;

        if (this.dynamicForm) {
          this.dynamicForm.resetWithValues(this.questions);
        }
        this.loader.hide();
      });
  };

  private shouldHideReseller = (): void => {
    if (this.currentUser.isCompanyOrViewAsCompany()) {
      this.hideReseller = true;
    } else {
      const partner = this.contract.company.partner;
      this.hideReseller = !partner.isResellerNeeded;
    }
  };

  private shouldOnlyEditReseller = (): void => {
    this.onlyReseller = this.contract.maintenanceProvider.isChannelWorks && this.currentUser.isPartnerOrViewAsPartner();
  };

  private resetTabs = (): void => {
    setTimeout(() => this.tables.forEach((table) => table.resetScrollbar()), 0);
  };

  private updateStatusAndDates = (event: AnyObject): void => {
    if (event.key === "endDate" || event.key === "startDate") {
      if (event.key === "startDate") {
        this.dynamicForm.getQuestion("endDate").minDate = event.value;
      }

      this.dynamicForm.replaceOrAppendQuestion(
        this.createContractService.generateContractStatusQuestion(
          "status",
          "Status",
          8,
          this.dynamicForm.form.get("startDate").value,
          this.dynamicForm.form.get("endDate").value,
        ),
      );
    }

    if (event.source?.id === "status") {
      const statusValue = event.value;

      this.dynamicForm.replaceOrAppendQuestion(
        this.createContractService.createDateQuestion({
          contract: this.contract,
          statusValue,
          onlyReseller: this.onlyReseller,
        }),
      );
      if (statusValue === ContractStatus.pending) {
        this.dynamicForm.form.get("startDate").markAllAsTouched();
      }
    }
  };

  /** Check if can add, update, link, unlink or edit Assets */
  private canModifyAssets = (): boolean => {
    if (!this.contract.maintenanceProvider?.isChannelWorks || this.currentUser.isGlobal())
      return allowUpdateEntity(this.contract, this.currentUser);
    return false;
  };

  private getWelcomeLetter = async (): Promise<void> => {
    this.contractService.getWelcomeLetter(this.contract.id).subscribe((response: any) => {
      this.savePdfFile(response, this.contract);
    });
  };

  private savePdfFile(blob: Blob, contract: Contract) {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `Welcome-Template-${contract.name}.pdf`;
    link.click();
  }

  private async performAdditionalOperations(): Promise<void> {
    this.shouldHideReseller();
    this.shouldOnlyEditReseller();
    this.canAddOrUpdateAttachment();
    this.buildForm(this.contract);
    this.setTabs();
  }

  private handleContractError(error: any): void {
    this.router.navigate(["/contracts"]);
    this.notificationService.error(error || "Couldn't find Contract");
  }

  //#endregion
}
